<template>
  <div class="space-y-4">
    <p class="text-2xl font-medium">{{ t('title') }}</p>
    <p class="text-sm text-obsidian-600">{{ t('hint') }}</p>

    <div class="grid grid-cols-2 gap-4">
      <SelectField
        class="col-span-2"
        name="accountIban"
        :label="t('accountIban')"
        :options="accountOptions"
      />
      <InputField name="amount" type="number" :max-fraction-digits="2" :label="t('amount')" />
      <DatepickerField name="bookedDate" :label="t('bookedDate')" />
      <InputField name="counterpartyName" :label="t('counterpartyName')" />
      <InputField name="counterpartyIban" :label="t('counterpartyIban')" />
      <InputField class="col-span-2" name="remittance" :label="t('remittance')" />
      <InputField class="col-span-2" name="endToEndId" :label="t('endToEndId')" />
    </div>

    <p v-if="isNotIngested" class="text-sm text-red-700">{{ t('notIngested') }}</p>

    <Divider />

    <div class="flex justify-end">
      <CVButton variant="default" :is-loading="isPending" @click.prevent="submit">
        {{ t('create') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { DateTime } from 'luxon';
import CVButton from '@/components/buttons/CVButton.vue';
import Divider from '@/components/Divider.vue';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import DatepickerField from '@/components/DatepickerField.vue';
import { useLegalEntities } from '@/queries/use-legal-entities';
import { useIngestBankStatements } from '@/queries/use-bank-statements';

const emit = defineEmits<{
  (e: 'complete'): void;
}>();

const { t } = useI18n();

const legalEntitiesQuery = useLegalEntities();
// The account IBANs of the legal entities, since an entry only lands on a known account.
const accountOptions = computed(() =>
  (legalEntitiesQuery.data.value ?? [])
    .filter((entity) => entity.iban)
    .map((entity) => ({ label: `${entity.name} · ${entity.iban}`, value: entity.iban })),
);

const { mutateAsync: ingest, isPending } = useIngestBankStatements();
const isNotIngested = ref(false);

const { handleSubmit, setFieldValue, values } = useForm({
  initialValues: {
    accountIban: undefined as string | undefined,
    amount: undefined as number | undefined,
    bookedDate: new Date(),
    counterpartyName: '',
    counterpartyIban: '',
    remittance: '',
    endToEndId: '',
  },
  validationSchema: toTypedSchema(
    yup.object({
      accountIban: yup.string().required(),
      amount: yup.number().required().notOneOf([0]),
      bookedDate: yup.date().required(),
      counterpartyName: yup.string().nullable(),
      counterpartyIban: yup.string().nullable(),
      remittance: yup.string().nullable(),
      endToEndId: yup.string().nullable(),
    }),
  ),
});

watch(
  accountOptions,
  (options) => {
    if (!values.accountIban && options[0]) setFieldValue('accountIban', options[0].value);
  },
  { immediate: true },
);

// Posted like a bank sync would, with a reference of its own so it is always a new entry.
const submit = handleSubmit(async (form) => {
  const result = await ingest({
    source: 'ros-test-entry',
    accountIban: form.accountIban,
    transactions: [
      {
        id: `TEST-${crypto.randomUUID()}`,
        amount: Math.abs(form.amount),
        creditDebit: form.amount < 0 ? 'DBIT' : 'CRDT',
        currency: 'EUR',
        date: DateTime.fromJSDate(form.bookedDate).toISODate(),
        reference: form.remittance || null,
        endToEndId: form.endToEndId || null,
        counterparty: {
          name: form.counterpartyName || null,
          iban: form.counterpartyIban || null,
        },
      },
    ],
  });
  isNotIngested.value = result.ingested === 0;
  if (!isNotIngested.value) emit('complete');
});
</script>

<i18n lang="json">
{
  "en": {
    "notIngested": "The entry was not created. Check the account IBAN and the booked date.",
    "title": "Create test entry",
    "hint": "Only in test environments: creates a Bank statement entry as if the bank had sent it.",
    "accountIban": "Account IBAN",
    "amount": "Amount in EUR (negative for a debit)",
    "bookedDate": "Booked date",
    "counterpartyName": "Payer name",
    "counterpartyIban": "Payer IBAN",
    "remittance": "Remittance",
    "endToEndId": "End-to-end ID",
    "create": "Create entry"
  },
  "de": {
    "notIngested": "Der Eintrag wurde nicht angelegt. Prüfe Konto-IBAN und Buchungsdatum.",
    "title": "Testeintrag anlegen",
    "hint": "Nur in Testumgebungen: legt eine Kontoauszugszeile an, als hätte die Bank sie geschickt.",
    "accountIban": "Konto-IBAN",
    "amount": "Betrag in EUR (negativ für eine Lastschrift)",
    "bookedDate": "Buchungsdatum",
    "counterpartyName": "Name Zahler",
    "counterpartyIban": "IBAN Zahler",
    "remittance": "Verwendungszweck",
    "endToEndId": "End-to-End-ID",
    "create": "Eintrag anlegen"
  },
  "es": {
    "notIngested": "No se creó la entrada. Revisa el IBAN de la cuenta y la fecha de contabilización.",
    "title": "Crear entrada de prueba",
    "hint": "Solo en entornos de prueba: crea una línea de extracto como si la hubiera enviado el banco.",
    "accountIban": "IBAN de la cuenta",
    "amount": "Importe en EUR (negativo para un cargo)",
    "bookedDate": "Fecha de contabilización",
    "counterpartyName": "Nombre del pagador",
    "counterpartyIban": "IBAN del pagador",
    "remittance": "Concepto",
    "endToEndId": "ID de extremo a extremo",
    "create": "Crear entrada"
  }
}
</i18n>
